/**
 * Step 4 helper — trainer availability + session create (LiveKit or Google Meet).
 * Keeps ScheduleSession focused on UI; all side effects live here.
 *
 * Steps:
 *   4a) POST /trainer/availability/  — block conflicts
 *   4b) POST /session/create-sessionsep/ with session_provider from draft
 *       + slim topic_plan (quiz rules come from platform contents)
 *       + day_schedules for workshops (day-wise Start/Join)
 */

import { authService } from '../../../utils/authService';
import type { CreateSessionDraft } from './createSessionFlowStorage';
import { getRolePrefix } from './createRoutes';
import { loadPlannerTopics } from './topicPlannerStorage';
import { toBackendTopicPlan } from './codeliveQuizDefaults';
import { normalizeTopicQuizPlan } from '../types/topicQuiz';
import { isWorkshopCategory } from './sessionCategoryUtils';
import {
  computeWorkshopSchedule,
  type WorkshopDayTiming,
} from './workshopSessionUtils';
import {
  conflictSessionId,
  findSelfTrainerConflict,
  otherTrainerConflicts,
} from './trainerAvailability';
export type CreateLiveKitSessionInput = {
  draft: CreateSessionDraft;
  title: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  qaDurationMinutes?: number;
};

export type CreateLiveKitSessionResult =
  | { ok: true; sessionId: number; inviteType: 'group' | 'personal'; rolePrefix: '/admin' | '/trainer' }
  | { ok: false; error: string };

function withSeconds(time: string): string {
  return time.length === 5 ? `${time}:00` : time;
}

function buildWorkshopDaySchedulesPayload(
  draft: CreateSessionDraft,
  startDate: string,
  startTime: string,
): Array<Record<string, unknown>> | undefined {
  if (!isWorkshopCategory(draft.sessionCategory)) return undefined;

  const plannerTopics = loadPlannerTopics();
  const durationByTopic: Record<string, number> = {};
  for (const topic of plannerTopics) {
    if (topic.title && topic.durationMinutes > 0) {
      durationByTopic[topic.title] = topic.durationMinutes;
    }
  }

  let dayTopics = draft.dayTopics;
  if (!dayTopics?.length) {
    const byDay = new Map<number, string[]>();
    for (const topic of plannerTopics) {
      const day =
        typeof topic.workshopDayIndex === 'number' ? topic.workshopDayIndex : 0;
      const list = byDay.get(day) || [];
      list.push(topic.title);
      byDay.set(day, list);
    }
    const maxDay = Math.max(0, ...Array.from(byDay.keys()), 0);
    dayTopics = Array.from({ length: maxDay + 1 }, (_, i) => byDay.get(i) || []);
  }
  if (!dayTopics.length) {
    dayTopics = [['Topic']];
  }

  const schedule = computeWorkshopSchedule(startDate, startTime, dayTopics, {
    dayTimings: draft.daySchedules as WorkshopDayTiming[] | undefined,
    durationByTopic,
  });

  return schedule.daySchedules.map((day) => ({
    day_index: day.dayIndex,
    day_label: day.dayLabel,
    start_date: day.startDate,
    start_time: day.startTime,
    end_date: day.endDate,
    end_time: day.endTime,
    duration_minutes: day.durationMinutes,
    status: 'scheduled',
  }));
}

export async function createLiveKitSessionFromDraft(
  input: CreateLiveKitSessionInput,
): Promise<CreateLiveKitSessionResult> {
  const { draft, title, startDate, startTime, endDate, endTime, qaDurationMinutes } = input;
  const trainerId = draft.trainerId || sessionStorage.getItem('Id') || '';

  try {
    //-------------------------------------------------
    // step:1 block conflicts before create (exclude same-title self)
    //-------------------------------------------------
    const availability = await authService.post('/trainer/availability/', {
      trainer_id: trainerId,
      start_date: startDate,
      end_date: endDate,
      start_time: withSeconds(startTime),
      end_time: withSeconds(endTime),
      session_id: '',
    });

    const rawConflicts = availability?.data?.list_of_sessions || [];
    const selfConflict = findSelfTrainerConflict(rawConflicts, title);
    const excludeId = conflictSessionId(selfConflict);
    const blocking = otherTrainerConflicts(rawConflicts, title);

    if (availability?.data?.success !== true && blocking.length > 0) {
      return {
        ok: false,
        error:
          availability?.data?.message ||
          'Trainer is not available at this time. Adjust the schedule and try again.',
      };
    }

    // Same auto-name already exists → reschedule that session instead of create.
    if (excludeId) {
      //-------------------------------------------------
      // step:1b reschedule existing same-title session
      //-------------------------------------------------
      const update = await authService.put('/session/update/', {
        session_id: excludeId,
        start_date: startDate,
        start_time: startTime,
        end_date: endDate,
        end_time: endTime,
        session_assigned_to: trainerId,
      });
      if (update?.status && update.status >= 400) {
        return {
          ok: false,
          error: 'Failed to update session timing. Please try again.',
        };
      }
      const inviteType =
        draft.sessionCategory === 'optional'
          ? 'personal'
          : draft.sessionType || 'group';
      return {
        ok: true,
        sessionId: Number(excludeId) || 0,
        inviteType,
        rolePrefix: getRolePrefix(),
      };
    }

    //-------------------------------------------------
    // step:2 slim topic plan — always normalize quizzes first so every
    // subtopic has postQuiz (required) and optional preQuiz before serialize
    //-------------------------------------------------
    const topicPlan = toBackendTopicPlan(
      normalizeTopicQuizPlan(loadPlannerTopics()),
    );
    const daySchedules = buildWorkshopDaySchedulesPayload(draft, startDate, startTime);

    //-------------------------------------------------
    // step:3 create LiveKit session
    //-------------------------------------------------
    const provider = 'livekit';
    const meetlink = '';

    const inviteType =
      draft.sessionCategory === 'optional'
        ? 'personal'
        : draft.sessionType || 'group';
    const sessionSettings = {
      waiting_room_enabled: Boolean(draft.waitingRoomEnabled),
      recording_enabled: draft.recordingEnabled !== false,
      join_defaults: {
        trainer: {
          microphone: Boolean(draft.trainerDefaultMic),
          camera: Boolean(draft.trainerDefaultCamera),
        },
        student: {
          microphone: Boolean(draft.studentDefaultMic),
          camera: Boolean(draft.studentDefaultCamera),
        },
      },
    };

    const response = await authService.post('/session/create-sessionsep/', {
      Session_Topic: title,
      Date: startDate,
      Start_Time: startTime,
      end_date: endDate,
      end_time: endTime,
      conductedby: sessionStorage.getItem('Id') || '',
      meetlink,
      session_provider: provider,
      live_session_category: draft.sessionCategory || 'mandatory',
      practice_test_enabled: Boolean(draft.practiceTestEnabled),
      subject: (draft.subjects || []).join(', '),
      track_id: draft.trackId ? [draft.trackId] : [],
      course_id: draft.courseId ? [draft.courseId] : [],
      batch_id: (() => {
        const ids =
          Array.isArray(draft.batchIds) && draft.batchIds.length > 0
            ? draft.batchIds
            : draft.batchId
              ? [draft.batchId]
              : [];
        return ids.map((id) => String(id).trim()).filter(Boolean);
      })(),
      subject_id: (draft.subjectIds || []).length
        ? draft.subjectIds
        : draft.subjects || [],
      session_assigned_to: trainerId,
      session_type: inviteType,
      Colleges: [] as string[],
      Branches: [] as string[],
      topic_plan: topicPlan,
      qa_duration_minutes: qaDurationMinutes ?? 10,
      ...(sessionSettings ? { session_settings: sessionSettings } : {}),
      ...(daySchedules?.length
        ? {
            day_schedules: daySchedules,
            workshop_day_count: daySchedules.length,
          }
        : {}),
      ...(draft.fromCoursePlan && draft.planSessionKey
        ? {
            plan_session_key: draft.planSessionKey,
            ...(draft.courseLiveSessionPlanId
              ? { course_live_session_plan_id: draft.courseLiveSessionPlanId }
              : {}),
          }
        : {}),
    });

    if (response?.data?.success !== true) {
      const apiMessage = String(response?.data?.message || '').trim();
      return {
        ok: false,
        error: apiMessage || 'Failed to create session. Please try again.',
      };
    }

    //-------------------------------------------------
    // step:4 return ids for navigate (add-students or Upcoming)
    //-------------------------------------------------
    return {
      ok: true,
      sessionId: Number(response.data.session_id) || 0,
      inviteType,
      rolePrefix: getRolePrefix(),
    };
  } catch (err) {
    console.error('CodeLive create failed', err);
    return {
      ok: false,
      error: 'An error occurred while creating the session. Please try again.',
    };
  }
}
